import { logger } from '../logger';

/**
 * Normalizes input strings for better matching
 * - Converts to lowercase
 * - Removes special characters
 * - Handles common variations
 */
export function normalizeInput(input: string): string {
  try {
    return input
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  } catch (error) {
    logger.error('Error normalizing input:', error);
    return String(input || '').toLowerCase().trim();
  }
}

// Common variations mapping
const commonVariations: Record<string, string[]> = {
  // Retail variations
  'retail': [
    'store', 'shop', 'retail store', 'retail shop',
    'drug store', 'pharmacy', 'drugstore',
    'liquor store', 'liquor shop', 'wine shop',
    'convenience store', 'corner store',
    'grocery store', 'supermarket',
    'boutique', 'gift shop'
  ],

  'ecommerce': ['e-commerce', 'online store', 'online shop'],
  'crm': ['customer relationship', 'client management'],
  'real-estate': ['property management', 'real estate', 'realty', 'property', 'rental', 'landlord', 'leasing', 'property manager', 'rental property'],
  'automation': ['automated', 'automate', 'workflow'],
  'marketing': ['promotion', 'advertising', 'digital marketing'],
  'accounting': ['bookkeeping', 'finances', 'financial'],
  'project management': ['task management', 'project tracking'],
  'communication': ['chat', 'messaging', 'collaboration'],
  'analytics': ['reporting', 'metrics', 'statistics'],
  'scheduling': ['calendar', 'appointments', 'booking'],
  'property management': ['rental management', 'property manager', 'landlord software', 'rental property', 'property maintenance'],
  'service business': ['service provider', 'service company', 'service industry', 'field service'],
  'consulting': ['consultant', 'advisor', 'professional services'],
  'freelance': ['independent contractor', 'self employed', 'freelancer'],

  // Industry-specific variations
  'healthcare': ['pharmacy', 'drug store', 'medical', 'health', 'wellness'],
  'food-beverage': ['restaurant', 'cafe', 'bar', 'liquor store', 'wine shop', 'grocery'],
  'retail-specialty': ['boutique', 'specialty store', 'gift shop', 'specialty retail'],

  // Goals variations
  'increase-sales': ['grow revenue', 'boost sales', 'increase revenue', 'make more money', 'generate more leads'],
  'improve-marketing': ['better marketing', 'marketing improvement', 'marketing automation', 'lead generation'],
  'enhance-customer-service': ['better customer service', 'customer support', 'customer experience', 'client satisfaction'],
  'streamline-operations': ['improve efficiency', 'optimize workflow', 'better processes', 'streamline workflow'],
  'reduce-costs': ['save money', 'cost reduction', 'lower expenses', 'budget optimization'],
  'build-brand': ['brand awareness', 'brand building', 'brand recognition', 'brand growth'],
  'expand-customer-base': ['get more customers', 'grow customer base', 'find new clients', 'customer acquisition'],
  'automate-tasks': ['automation', 'automated workflow', 'reduce manual work', 'process automation'],
  'manage-scheduling': ['appointment scheduling', 'calendar management', 'booking system'],
  'track-jobs': ['job tracking', 'project tracking', 'work tracking', 'task management'],

  // Features variations
  'ease-of-use': ['user friendly', 'simple to use', 'intuitive', 'easy to learn'],
  'integrations': ['connect with other tools', 'tool integration', 'works with other software'],
  'automation-features': ['automated tasks', 'workflow automation', 'automatic'],
  'mobile': ['mobile app', 'phone app', 'mobile access', 'mobile friendly'],
  'support': ['customer support', 'technical support', 'help desk'],
  'security': ['secure', 'data protection', 'privacy'],
  'scalability': ['scalable', 'grow with business', 'expandable'],
  'scheduling-features': ['appointment booking', 'calendar', 'scheduling system'],
  'invoicing': ['billing', 'payment processing', 'send invoices'],
  'job-tracking': ['track projects', 'job management', 'work tracking'],
  'field-service': ['field work', 'on-site service', 'mobile workforce']
};

/**
 * Extracts keywords from a longer text input
 */
export function extractKeywords(text: string): string[] {
  const normalized = normalizeInput(text);
  const words = normalized.split(' ');
  const phrases: string[] = [];

  // Check for multi-word matches first
  for (let i = 0; i < words.length - 1; i++) {
    phrases.push(words[i] + ' ' + words[i + 1]);
    if (i < words.length - 2) {
      phrases.push(words[i] + ' ' + words[i + 1] + ' ' + words[i + 2]);
    }
  }
  
  // Find multi-word matches first
  const keywords: string[] = [];
  for (const [key, variations] of Object.entries(commonVariations)) {
    if (variations.some(v => 
      normalized.includes(normalizeInput(v)) || 
      phrases.some(phrase => areTermsSimilar(phrase, v))
    )) {
      keywords.push(key);
    }
  }
  
  // Add individual relevant words
  words.forEach(word => {
    if (word.length > 3) { // Skip very short words
      keywords.push(word);
    }
  });
  
  return [...new Set(keywords)]; // Remove duplicates
}
/**
 * Checks if two terms are related based on common variations
 */
export function areTermsRelated(term1: string, term2: string): boolean {
  const normalized1 = normalizeInput(term1);
  const normalized2 = normalizeInput(term2);

  // Direct match
  if (normalized1 === normalized2) return true;

  // Check variations
  for (const [key, variations] of Object.entries(commonVariations)) {
    const normalizedKey = normalizeInput(key);
    const isFirstTermMatch = normalized1 === normalizedKey || variations.some(v => normalized1.includes(normalizeInput(v)));
    const isSecondTermMatch = normalized2 === normalizedKey || variations.some(v => normalized2.includes(normalizeInput(v)));

    if (isFirstTermMatch && isSecondTermMatch) return true;
  }

  return false;
}

/**
 * Check if two terms are similar using fuzzy matching
 */
function areTermsSimilar(term1: string, term2: string): boolean {
  const norm1 = normalizeInput(term1);
  const norm2 = normalizeInput(term2);

  // Direct match
  if (norm1 === norm2) return true;

  // Partial match
  if (norm1.includes(norm2) || norm2.includes(norm1)) return true;

  // Words in common
  const words1 = new Set(norm1.split(' '));
  const words2 = new Set(norm2.split(' '));
  const commonWords = [...words1].filter(word => words2.has(word));
  
  // If more than 50% words match
  if (commonWords.length >= Math.min(words1.size, words2.size) * 0.5) {
    return true;
  }

  return false;
}